import { configureStore as configureStoreToolkit } from '@reduxjs/toolkit';
import { routerMiddleware } from 'connected-react-router';
import createSagaMiddleware from 'redux-saga';
import * as Sentry from '@sentry/react';

import { companiesApi } from 'services/companies';
import { analyticsApi } from 'services/analytics';
import { networkApi } from 'services/network';
import { networkV2Api } from 'services/networkV2';
import { scrubForSentry } from '../helpers/sentryRedaction';
import { contactsApi } from '../services/contacts';
import { setActiveNetwork } from '../actions/networksActions';
import { networkAdminsApi } from '../services/networkAdmins';
import { organizationApiV2 } from '../services/organizationsV2';
import { hiresApi } from '../services/hires';
import { hiresV2Api } from '../services/hiresV2';
import { tagsApi } from '../services/tags';
import { skillsApi } from '../services/skills';
import { locationsApi } from '../services/locations';
import { apiKeysApiV2 } from '../services/apiKeysV2';
import createReducer from './reducers';
import { contactsV2Api } from '../services/contactsV2';
import { companiesV2Api } from '../services/companiesV2';
import { userApi } from '../services/user';
import { jobsV2Api } from '../services/jobsV2';
import { connectJobsAPI } from '../services/connectJobs';
import { searchApi } from '../services/search';
import { sharedListsApi } from '../services/sharedLists';
import { sharedListsApiV2 } from '../services/sharedListV2';
import { sharedListViewsV2Api } from '../services/sharedListViewsV2';
import { userApi2 } from '../services/userV2';
import { networkAdminsApiV2 } from '../services/networkAdminV2';
import { introsV2Api } from '../services/intros';
import { sharedCompanyListV2Api } from '../services/sharedCompanyListV2';
import { uploadFileV2Api } from '../services/uploadFileV2';
import { authApi } from '../services/auth';
import { organizationApi } from '../services/organizations';
import { alliesApi } from '../services/allies';
import { customFieldsV2Api } from '../services/customFieldsV2';
import { listsTreeApi } from '../services/listsTree';
import { companyAtsApi } from '../services/companyAtsV2';
import { userGoogleAccountsApi } from '../services/userGoogleAccountsV2';
import { connectionPathsV2Api } from '../services/connectionPathsV2';
import { findemApiV2 } from '../services/findemV2';
import { signupFormsApi, publicSignupFormsApi } from '../services/signupForms';
import { networkMovementPreferencesApi } from '../services/networkMovementPreferences';
import { movementsApi } from '../services/movements';
import { talentAttractionApi } from '../services/talentAttraction';

// Sensitive-key handling lives in `helpers/sentryRedaction` so every telemetry
// surface (this enhancer, `services/request.js`) composes the same scrubbers.
// See that module for the rationale on key-list ownership.

export default function configureStore(initialState = {}, history) {
  const reduxSagaMonitorOptions = {};

  // RTK Query mutation actions carry the raw mutation args under
  // `meta.arg.originalArgs`, which can include OAuth codes and user-entered
  // PII (see `helpers/sentryRedaction`). The base `removeKeys` scrubber only
  // walks `payload`, so without an explicit meta-arg pass those values would
  // leak as Redux breadcrumbs even when no exception ever fires.
  // The enhancer rebuilds `transformed` from a minimal object instead of
  // spreading the raw action. Spreading copied `meta` verbatim, so any RTK
  // Query / saga action with `meta` but no `originalArgs` (or any future
  // middleware that adds PII to `meta`) leaked through. Building up means
  // only sanitized fields ever reach Sentry breadcrumbs.
  const sentryReduxEnhancer = Sentry.createReduxEnhancer({
    actionTransformer: (action) => {
      switch (action.type) {
        case '@@INIT':
        case setActiveNetwork().type:
          return null;
        default: {
          const transformed = { type: action.type };
          if (action.payload !== undefined) {
            transformed.payload = scrubForSentry(action.payload);
          }
          if (action.meta !== undefined) {
            // Both scrubbers are required and their key sets are disjoint:
            // `redactSensitiveKeys` covers OAuth / exclusion auth material
            // (`SENSITIVE_BODY_KEYS`), `removeKeys(restrictedKeys)` strips
            // broad PII (email, first/lastName, currentLocation, …). Running
            // both over the whole meta also covers RTK Query "pending"
            // actions whose body has no `originalArgs` yet.
            transformed.meta = scrubForSentry(action.meta);
          }
          if (transformed.payload === undefined && transformed.meta === undefined) {
            return null;
          }
          return transformed;
        }
      }
    },
    // RTK Query keeps the args of in-flight mutations on the state slice
    // for the lifetime of the request, so the `actionTransformer` pass
    // alone is not enough: an exception captured mid-mutation snapshots
    // the state and ships `originalArgs` (OAuth code, exclusion lists,
    // etc.) to Sentry. The composed scrubber covers both surfaces.
    stateTransformer: scrubForSentry,
  });

  const sagaMiddleware = createSagaMiddleware(reduxSagaMonitorOptions);

  const store = configureStoreToolkit({
    preloadedState: initialState,
    reducer: createReducer(),
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware().concat(
        sagaMiddleware,
        routerMiddleware(history),
        contactsApi.middleware,
        companiesApi.middleware,
        contactsV2Api.middleware,
        tagsApi.middleware,
        networkAdminsApi.middleware,
        networkAdminsApiV2.middleware,
        organizationApiV2.middleware,
        hiresApi.middleware,
        hiresV2Api.middleware,
        analyticsApi.middleware,
        userApi.middleware,
        userApi2.middleware,
        jobsV2Api.middleware,
        companiesV2Api.middleware,
        skillsApi.middleware,
        locationsApi.middleware,
        connectJobsAPI.middleware,
        searchApi.middleware,
        sharedListsApi.middleware,
        sharedListsApiV2.middleware,
        sharedListViewsV2Api.middleware,
        networkApi.middleware,
        networkV2Api.middleware,
        introsV2Api.middleware,
        sharedCompanyListV2Api.middleware,
        uploadFileV2Api.middleware,
        authApi.middleware,
        apiKeysApiV2.middleware,
        organizationApi.middleware,
        alliesApi.middleware,
        customFieldsV2Api.middleware,
        listsTreeApi.middleware,
        companyAtsApi.middleware,
        userGoogleAccountsApi.middleware,
        connectionPathsV2Api.middleware,
        findemApiV2.middleware,
        signupFormsApi.middleware,
        publicSignupFormsApi.middleware,
        networkMovementPreferencesApi.middleware,
        movementsApi.middleware,
        talentAttractionApi.middleware,
      ),
    enhancers: (getDefaultEnhancers) => getDefaultEnhancers().concat(sentryReduxEnhancer),
  });

  // Extensions
  store.runSaga = sagaMiddleware.run;
  store.injectedReducers = {}; // Reducer registry
  store.injectedSagas = {}; // Saga registry

  // Make reducers hot reloadable, see http://mxs.is/googmo
  /* istanbul ignore next */
  if (import.meta.hot) {
    import.meta.hot.accept('./reducers', () => {
      store.replaceReducer(createReducer(store.injectedReducers));
    });
  }

  return store;
}
